/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ReactNode } from 'react';
import Heading from '@atlaskit/heading/heading';
import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { SideNavHeader, SideNavBody } from '@atlaskit/navigation-system/layout/side-nav';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { BackButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-back-button/src/ui/back-button/index.tsx';
import { AsyncPageSwitcher } from '../../common/ui/page-switcher/index.tsx';
import type { PageName } from '../../common/ui/page-switcher/types.tsx';
import { SidebarHeading } from './SidebarHeading.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-common.sidebar-content.heading',
	defaultMessage: 'Jira admin settings',
	description: 'Heading for the global jira settings pages',
});

type Props = {
	children: ReactNode;
	currentPageName: PageName;
	testId?: string;
};

export const SidebarContent = ({
	children,
	currentPageName,
	testId = 'navigation-apps-sidebar-nav4-sidebars-settings-global-common.ui.sidebar-content',
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<>
			<SideNavHeader>
				<Stack space="space.200" xcss={headingStyles}>
					<Inline
						alignBlock="center"
						grow="hug"
						space="space.100"
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="navigation-apps-sidebar-nav4-sidebars-settings-global-common.ui.sidebar-content.header"
					>
						<BackButton />
						{fg('enable_jsm_admin') ? (
							<Placeholder
								name="nav4.sidebar.global-settings.heading"
								fallback={<Box xcss={headingSkeletonStyles} />}
							>
								<SidebarHeading />
							</Placeholder>
						) : (
							<Heading size="small" as="h2">
								{formatMessage(heading)}
							</Heading>
						)}
					</Inline>

					<AsyncPageSwitcher currentPageName={currentPageName} />
				</Stack>
			</SideNavHeader>
			<SideNavBody testId={testId}>
				<MenuList>{children}</MenuList>
			</SideNavBody>
		</>
	);
};

const headingStyles = xcss({
	// 4px of left padding is added to align the back icon with the side nav toggle button
	paddingInlineStart: 'space.050',
});

// Shimmer shown while the JSM-admin permission resolves, so the heading never briefly displays the
// wrong title (matches the page switcher's skeleton treatment).
const headingSkeletonStyles = xcss({
	backgroundColor: 'color.skeleton',
	width: '120px',
	height: token('space.200'),
	borderRadius: 'radius.small',
});
